import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { dkGelismis } from "@/lib/api";
import { buildParcaPayload, flagsFromApiPart } from "@/lib/parcaIslemUtils";
import { ensureAllZones } from "../dk-gelismis/AracDiagram";
import { DK_DRAFT_KEY, FALLBACK_SABLONLAR } from "./constants";

const KM_BANDS = [
    { min: 0, max: 19999 },
    { min: 20000, max: 49999 },
    { min: 50000, max: 99999 },
    { min: 100000, max: 149999 },
    { min: 150000, max: 199999 },
    { min: 200000, max: 299999 },
    { min: 300000, max: 9999999 },
];

function formatApiHata(error) {
    if (!error) return "";
    if (Array.isArray(error)) return error.map(String).join(" ");
    return String(error);
}

async function parseBlobApiError(e, fallback = "İşlem başarısız") {
    const data = e?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            try {
                const json = JSON.parse(text);
                return formatApiHata(json.error) || text || fallback;
            } catch {
                return text || fallback;
            }
        } catch {
            return fallback;
        }
    }
    return formatApiHata(e?.response?.data?.error) || e.message || fallback;
}

function kmBandSuggest(kmVal) {
    const k = Number(kmVal);
    if (!Number.isFinite(k) || k < 0) return { min: "", max: "" };
    const b = KM_BANDS.find((x) => k >= x.min && k <= x.max) || KM_BANDS[KM_BANDS.length - 1];
    return { min: String(b.min), max: b.max >= 9999999 ? "500000" : String(b.max) };
}

function normalizeParca(p) {
    const payload = buildParcaPayload(p);
    if (!payload) return null;
    return { ...p, ...payload, diagram_id: p.diagram_id, parca_adi: p.parca_adi, etiket: p.etiket };
}

function parcaFromKayit(p, zonesById) {
    const diagramId = p.diagram_id || null;
    const zone = diagramId ? zonesById.get(diagramId) : null;
    const etiket = p.etiket || p.parca_adi || zone?.etiket || "Parça";
    const flags = flagsFromApiPart(p);
    const draft = {
        diagram_id: diagramId,
        parca_adi: etiket,
        etiket,
        parca_id: p.parca_id,
        ...flags,
    };
    return normalizeParca(draft);
}

export function useDkGelismis() {
    const [searchParams] = useSearchParams();
    const deepDosyaId = searchParams.get("dosya");
    const deepEkspertiz = searchParams.get("ekspertiz");

    const [loading, setLoading] = useState(false);
    const [diagram, setDiagram] = useState(null);
    const [seciliParcalar, setSeciliParcalar] = useState([]);
    const [hoverParca, setHoverParca] = useState(null);
    const [dosyalar, setDosyalar] = useState([]);
    const [dosyaQ, setDosyaQ] = useState("");
    const [seciliDosya, setSeciliDosya] = useState(null);
    const [gecmisHesaplar, setGecmisHesaplar] = useState([]);

    const [marka, setMarka] = useState("");
    const [model, setModel] = useState("");
    const [modelYili, setModelYili] = useState("");
    const [km, setKm] = useState("");
    const [kusur, setKusur] = useState("0");
    const [rayic, setRayic] = useState("");
    const [hasarFatura, setHasarFatura] = useState("");
    const [hasarDurumu, setHasarDurumu] = useState("orta");
    const [vites, setVites] = useState("manuel");
    const [yakit, setYakit] = useState("benzin");
    const [kmMin, setKmMin] = useState("");
    const [kmMax, setKmMax] = useState("");
    const [tramer, setTramer] = useState("");

    const [hesapYontemi, setHesapYontemi] = useState("karsilastirma");
    const [rayicHasarli, setRayicHasarli] = useState("");
    const [aracNiteligi, setAracNiteligi] = useState("hususi");
    const [rayicHasarliSonuc, setRayicHasarliSonuc] = useState(null);

    const [rayicSonuc, setRayicSonuc] = useState(null);
    const [hesap, setHesap] = useState(null);
    const [senaryo, setSenaryo] = useState(null);
    const [onizleme, setOnizleme] = useState(null);
    const [seciliIlan, setSeciliIlan] = useState(null);
    const [tahkimPaket, setTahkimPaket] = useState(null);
    const [diagramGorunum, setDiagramGorunum] = useState("ustten");
    const [ekspertizSonuc, setEkspertizSonuc] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const draftLoaded = useRef(false);
    const deepLoaded = useRef(false);

    const zones = useMemo(() => ensureAllZones(diagram?.parcalar || []), [diagram?.parcalar]);
    const zonesById = useMemo(() => new Map(zones.map((z) => [z.id, z])), [zones]);
    const sablonlar = diagram?.hasar_sablonlari || FALLBACK_SABLONLAR;
    const gorunumler = diagram?.gorunumler || [{ id: "ustten", etiket: "Üstten" }];

    const adim = seciliParcalar.length > 0 ? (rayic ? (hesap ? 4 : 3) : 2) : 1;

    const aracFiltreBody = useCallback(
        () => ({
            vites_tip: vites || undefined,
            yakit_tip: yakit || undefined,
            km_min: kmMin !== "" ? Number(kmMin) : undefined,
            km_max: kmMax !== "" ? Number(kmMax) : undefined,
            tramer_tutar: tramer !== "" ? Number(tramer) : undefined,
            hasar_durumu: hasarDurumu,
        }),
        [vites, yakit, kmMin, kmMax, tramer, hasarDurumu],
    );

    const hesapPayload = useCallback(
        (extra = {}) => ({
            marka,
            model,
            model_yili: Number(modelYili) || 2020,
            km: Number(km) || 0,
            kusur_orani: Number(kusur) || 0,
            rayic_deger: rayic ? Number(rayic) : undefined,
            hasar_faturasi: hasarFatura ? Number(hasarFatura) : undefined,
            parcalar: seciliParcalar.map(normalizeParca).filter(Boolean),
            hesap_yontemi: hesapYontemi,
            rayic_hasarli: rayicHasarli ? Number(rayicHasarli) : undefined,
            arac_niteligi: aracNiteligi,
            ...aracFiltreBody(),
            ...extra,
        }),
        [
            marka,
            model,
            modelYili,
            km,
            kusur,
            rayic,
            hasarFatura,
            seciliParcalar,
            hesapYontemi,
            rayicHasarli,
            aracNiteligi,
            aracFiltreBody,
        ],
    );

    const draftSnapshot = useMemo(
        () => ({
            marka,
            model,
            modelYili,
            km,
            kusur,
            rayic,
            hasarFatura,
            hasarDurumu,
            vites,
            yakit,
            kmMin,
            kmMax,
            tramer,
            hesapYontemi,
            rayicHasarli,
            aracNiteligi,
            seciliParcalar,
            seciliDosyaId: seciliDosya?.id || null,
        }),
        [
            marka,
            model,
            modelYili,
            km,
            kusur,
            rayic,
            hasarFatura,
            hasarDurumu,
            vites,
            yakit,
            kmMin,
            kmMax,
            tramer,
            hesapYontemi,
            rayicHasarli,
            aracNiteligi,
            seciliParcalar,
            seciliDosya,
        ],
    );

    useEffect(() => {
        let cancelled = false;
        const loadDiagram = (attempt = 0) => {
            dkGelismis
                .diagram()
                .then((data) => {
                    if (!cancelled) setDiagram(data);
                })
                .catch(() => {
                    if (cancelled) return;
                    if (attempt < 4) {
                        const delay = 1500 * (attempt + 1);
                        setTimeout(() => loadDiagram(attempt + 1), delay);
                        return;
                    }
                    toast.error("Diyagram yüklenemedi — sayfayı yenileyin");
                });
        };
        loadDiagram();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            dkGelismis
                .dosyalar({ q: dosyaQ || undefined, limit: 50 })
                .then((r) => setDosyalar(r.dosyalar || []))
                .catch(() => {});
        }, 280);
        return () => clearTimeout(t);
    }, [dosyaQ]);

    useEffect(() => {
        if (draftLoaded.current) return;
        draftLoaded.current = true;
        try {
            const raw = localStorage.getItem(DK_DRAFT_KEY);
            if (!raw || deepDosyaId) return;
            const d = JSON.parse(raw);
            if (d.marka) setMarka(d.marka);
            if (d.model) setModel(d.model);
            if (d.modelYili) setModelYili(d.modelYili);
            if (d.km) setKm(d.km);
            if (d.kusur != null) setKusur(String(d.kusur));
            if (d.rayic) setRayic(d.rayic);
            if (d.hasarFatura) setHasarFatura(d.hasarFatura);
            if (d.hasarDurumu) setHasarDurumu(d.hasarDurumu);
            if (d.vites) setVites(d.vites);
            if (d.yakit) setYakit(d.yakit);
            if (d.kmMin) setKmMin(d.kmMin);
            if (d.kmMax) setKmMax(d.kmMax);
            if (d.tramer) setTramer(d.tramer);
            if (d.hesapYontemi) setHesapYontemi(d.hesapYontemi);
            if (d.rayicHasarli) setRayicHasarli(d.rayicHasarli);
            if (d.aracNiteligi) setAracNiteligi(d.aracNiteligi);
            if (Array.isArray(d.seciliParcalar) && d.seciliParcalar.length) {
                setSeciliParcalar(d.seciliParcalar);
            }
        } catch {
            /* */
        }
    }, [deepDosyaId]);

    const applyGelismisForm = useCallback(
        (form, { mesaj = "Form dolduruldu" } = {}) => {
            if (form.marka) setMarka(form.marka);
            if (form.model) setModel(form.model);
            if (form.model_yili) setModelYili(String(form.model_yili));
            if (form.km != null) setKm(String(form.km));
            if (form.kusur_orani != null) setKusur(String(form.kusur_orani));
            if (form.rayic) setRayic(String(Math.round(Number(form.rayic))));
            if (form.hasar_faturasi) setHasarFatura(String(Math.round(Number(form.hasar_faturasi))));
            if (form.hasar_durumu) setHasarDurumu(form.hasar_durumu);
            const parcalar = (form.parcalar || [])
                .map((p) => {
                    if (!p.diagram_id && p.parca_adi) {
                        const z = zones.find(
                            (x) =>
                                x.etiket?.toLowerCase() === String(p.parca_adi).toLowerCase() ||
                                String(p.parca_adi).toLowerCase().includes(x.etiket?.toLowerCase()),
                        );
                        if (z) p.diagram_id = z.id;
                    }
                    return parcaFromKayit(p, zonesById);
                })
                .filter(Boolean);
            if (parcalar.length) setSeciliParcalar(parcalar);
            toast.success(`${mesaj} — ${parcalar.length} parça`);
            return parcalar.length;
        },
        [zones, zonesById],
    );

    const applyDosyaDetay = useCallback(
        (det) => {
            const dos = det.dosya || {};
            setSeciliDosya(dos);
            setMarka(dos.marka || "");
            setModel(dos.model || "");
            setModelYili(dos.model_yili ? String(dos.model_yili) : "");
            setKm(dos.km != null ? String(dos.km) : "");
            setKusur(dos.kusur_orani != null ? String(dos.kusur_orani) : "0");
            if (dos.arac_rayic_degeri) setRayic(String(Math.round(dos.arac_rayic_degeri)));
            if (dos.hasar_faturasi) setHasarFatura(String(Math.round(dos.hasar_faturasi)));
            setGecmisHesaplar(det.gecmis_hesaplar || []);
            const kayitli = (det.kayitli_parcalar || [])
                .map((p) => parcaFromKayit(p, zonesById))
                .filter(Boolean);
            if (kayitli.length) {
                setSeciliParcalar(kayitli);
                toast.success(`${kayitli.length} kayıtlı parça yüklendi`);
            }
        },
        [zonesById],
    );

    const ekspertizdenDoldur = useCallback(
        async (dosyaId, { hizli = false } = {}) => {
            const id = dosyaId || seciliDosya?.id;
            if (!id) {
                toast.error("Önce dosya seçin");
                return;
            }
            setLoading(true);
            try {
                const r = await dkGelismis.ekspertizForm(id, hizli ? { hizli: 1 } : {});
                if (!r.success) throw new Error(r.error || "Ekspertiz formu alınamadı");
                applyGelismisForm(r.gelismis_form || {}, { mesaj: "Ekspertiz evrakından yüklendi" });
            } catch (e) {
                toast.error(e?.response?.data?.error || e.message || "Ekspertiz aktarımı başarısız");
            } finally {
                setLoading(false);
            }
        },
        [seciliDosya?.id, applyGelismisForm],
    );

    const sunucuTaslakKaydet = useCallback(async () => {
        if (!seciliDosya?.id) return;
        try {
            await dkGelismis.taslakKaydet({ dosya_id: seciliDosya.id, taslak: draftSnapshot });
        } catch {
            /* sessiz */
        }
    }, [seciliDosya?.id, draftSnapshot]);

    useEffect(() => {
        const t = setTimeout(() => {
            try {
                localStorage.setItem(DK_DRAFT_KEY, JSON.stringify(draftSnapshot));
            } catch {
                /* */
            }
            if (seciliDosya?.id) sunucuTaslakKaydet();
        }, 1200);
        return () => clearTimeout(t);
    }, [draftSnapshot, seciliDosya?.id, sunucuTaslakKaydet]);

    const dosyaSec = useCallback(
        async (d) => {
            if (!d) {
                setSeciliDosya(null);
                setGecmisHesaplar([]);
                return;
            }
            setSeciliDosya(d);
            try {
                const det = await dkGelismis.dosya(d.id);
                applyDosyaDetay(det);
                try {
                    const tr = await dkGelismis.taslakYukle(d.id);
                    const t = tr.taslak;
                    if (t?.seciliParcalar?.length) {
                        setSeciliParcalar(t.seciliParcalar);
                        toast.info("Sunucu taslağı yüklendi");
                    }
                } catch {
                    /* */
                }
            } catch {
                toast.error("Dosya detayı alınamadı");
            }
        },
        [applyDosyaDetay],
    );

    useEffect(() => {
        if (!deepDosyaId || deepLoaded.current || !zones.length) return;
        deepLoaded.current = true;
        const id = Number(deepDosyaId);
        if (!id) return;
        dkGelismis
            .dosya(id)
            .then(async (det) => {
                applyDosyaDetay(det);
                toast.success("Dosya bağlantısından yüklendi");
                if (deepEkspertiz === "1" || deepEkspertiz === "true") {
                    await ekspertizdenDoldur(id);
                }
            })
            .catch(() => toast.error("Dosya yüklenemedi"));
    }, [deepDosyaId, deepEkspertiz, zones.length, applyDosyaDetay, ekspertizdenDoldur]);

    const parcaImza = useMemo(
        () =>
            JSON.stringify(
                seciliParcalar.map(normalizeParca).filter(Boolean).map((p) => ({
                    id: p.diagram_id,
                    t: p.islem_tipi,
                    o: p.onarim_seviyesi,
                    b: p.boya_tipi,
                    a: p.adet,
                })),
            ),
        [seciliParcalar],
    );

    const autoHesapRef = useRef(0);

    useEffect(() => {
        if (!seciliParcalar.length || !rayic) {
            setOnizleme(null);
            return undefined;
        }
        const gen = ++autoHesapRef.current;
        const t = setTimeout(async () => {
            try {
                const r = await dkGelismis.hesapla(hesapPayload());
                if (gen !== autoHesapRef.current) return;
                if (r.success === false) {
                    setOnizleme(null);
                    return;
                }
                setOnizleme({
                    tutar: r.gercek_deger_kaybi_fmt,
                    hasar_k: r.hasar_katsayisi,
                    hasar_k_parcadan: r.hasar_katsayisi_ham ?? r.hasar_katsayisi_parcadan,
                    parca: seciliParcalar.length,
                    mbs_fmt: r.gercek_ham_fmt,
                });
                setHesap(r);
            } catch {
                if (gen === autoHesapRef.current) setOnizleme(null);
            }
        }, 650);
        return () => clearTimeout(t);
    }, [
        parcaImza,
        rayic,
        kusur,
        km,
        hasarFatura,
        hasarDurumu,
        hesapPayload,
        marka,
        model,
        modelYili,
        hesapYontemi,
        rayicHasarli,
        aracNiteligi,
        seciliParcalar.length,
    ]);

    const kmOner = () => {
        const { min, max } = kmBandSuggest(km);
        if (min) setKmMin(min);
        if (max) setKmMax(max);
    };

    const parcaToggle = (zone) => {
        setSeciliParcalar((prev) => {
            const idx = prev.findIndex((p) => p.diagram_id === zone.id);
            if (idx >= 0) return prev.filter((p) => p.diagram_id !== zone.id);
            const vars = zone.varsayilan_islem || "onarim";
            const degisim = vars === "degisim";
            const boya = vars === "boya";
            const draft = {
                diagram_id: zone.id,
                parca_adi: zone.etiket,
                etiket: zone.etiket,
                islem_degisim: degisim,
                islem_onarim: !degisim && !boya,
                islem_boya: boya,
                onarim_seviyesi: "ORTA",
                boya_tipi: "TAM",
                adet: 1,
            };
            const payload = buildParcaPayload(draft);
            return [...prev, payload ? { ...draft, ...payload } : draft];
        });
        setHesap(null);
        setSenaryo(null);
    };

    const parcaIslemChange = (diagramId, islemState) => {
        setSeciliParcalar((prev) =>
            prev.map((p) => {
                if (p.diagram_id !== diagramId) return p;
                const merged = { ...p, ...islemState };
                return normalizeParca(merged) || merged;
            }),
        );
        setHesap(null);
        setSenaryo(null);
    };

    const uygulaSablon = (sablon) => {
        const yeni = [];
        for (const id of sablon.diagram_ids || []) {
            const zone = zonesById.get(id);
            if (!zone) continue;
            const vars = sablon.varsayilan_islem || zone.varsayilan_islem || "onarim";
            const degisim = vars === "degisim";
            const boya = vars === "boya";
            const draft = {
                diagram_id: id,
                parca_adi: zone.etiket,
                etiket: zone.etiket,
                islem_degisim: degisim,
                islem_onarim: !degisim && !boya,
                islem_boya: boya,
                onarim_seviyesi: "ORTA",
                boya_tipi: "TAM",
                adet: 1,
            };
            const payload = buildParcaPayload(draft);
            if (payload) yeni.push({ ...draft, ...payload });
        }
        setSeciliParcalar(yeni);
        setHesap(null);
        toast.success(`${sablon.etiket} şablonu uygulandı`);
    };

    const rayicHasarliAra = async () => {
        if (!marka.trim() || !rayic) {
            toast.error("Önce hasarsız rayiç girin veya piyasa ara");
            return;
        }
        setLoading(true);
        try {
            const r = await dkGelismis.rayic({
                marka,
                model,
                model_yili: Number(modelYili) || 2020,
                km: Number(km) || 0,
                hasar_faturasi: hasarFatura ? Number(hasarFatura) : undefined,
                rayic_hasarsiz: Number(rayic),
                profil: "hasarli",
                derin: true,
                ...aracFiltreBody(),
            });
            setRayicHasarliSonuc(r);
            if (r.onerilen_rayic) {
                setRayicHasarli(String(Math.round(r.onerilen_rayic)));
                toast.success(`Hasarlı rayiç: ${r.onerilen_rayic_fmt}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Hasarlı rayiç araması başarısız");
        } finally {
            setLoading(false);
        }
    };

    const rayicAra = async () => {
        if (!marka.trim()) {
            toast.error("Marka girin");
            return;
        }
        setLoading(true);
        try {
            const r = await dkGelismis.rayic({
                marka,
                model,
                model_yili: Number(modelYili) || 2020,
                km: Number(km) || 0,
                hasar_faturasi: hasarFatura ? Number(hasarFatura) : undefined,
                derin: true,
                profil: "hasarsiz",
                ...aracFiltreBody(),
            });
            setRayicSonuc(r);
            if (r.onerilen_rayic) {
                setRayic(String(Math.round(r.onerilen_rayic)));
                toast.success(`Önerilen rayiç: ${r.onerilen_rayic_fmt}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Rayiç araması başarısız");
        } finally {
            setLoading(false);
        }
    };

    const hesapla = async (piyasaRayicAra = false) => {
        if (!seciliParcalar.length) {
            toast.error("Araç görselinden en az bir parça seçin");
            return;
        }
        if (!rayic && !piyasaRayicAra) {
            toast.error("Rayiç girin veya tek adımda piyasa+hesap kullanın");
            return;
        }
        setLoading(true);
        try {
            const r = await dkGelismis.hesapla({
                ...hesapPayload(),
                piyasa_rayic_ara: piyasaRayicAra,
            });
            if (r.success === false) throw new Error(r.error);
            setHesap(r);
            if (r.rayic_arastirma) setRayicSonuc(r.rayic_arastirma);
            if (r.rayic_hasarli_arastirma) setRayicHasarliSonuc(r.rayic_hasarli_arastirma);
            if (r.rayic_hasarli && !rayicHasarli) setRayicHasarli(String(Math.round(Number(r.rayic_hasarli))));
            setSenaryo(null);
            const oneri = r.onerilen_yontem ? ` · önerilen: ${r.onerilen_yontem}` : "";
            toast.success(`Net hesap tamamlandı${oneri}`);
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Hesap başarısız");
        } finally {
            setLoading(false);
        }
    };

    const senaryoHesapla = async () => {
        if (!seciliParcalar.length || !rayic) {
            toast.error("Senaryo için parça ve rayiç gerekli");
            return;
        }
        setLoading(true);
        try {
            const r = await dkGelismis.senaryo(hesapPayload());
            if (r.success === false) throw new Error(r.error);
            setSenaryo(r);
            toast.success("Kusur senaryoları hazır");
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Senaryo başarısız");
        } finally {
            setLoading(false);
        }
    };

    const kaydet = async () => {
        if (!seciliDosya?.id) {
            toast.error("Kayıt için dosya seçin");
            return;
        }
        if (!hesap?.parcalar?.length) {
            toast.error("Önce net hesap yapın");
            return;
        }
        if (!window.confirm("Değer kaybı dosyaya kaydedilsin mi?")) return;
        setLoading(true);
        try {
            await dkGelismis.kaydet({
                dosya_id: seciliDosya.id,
                onay: true,
                parcalar: seciliParcalar.map((sp) => {
                    const h = (hesap.parcalar || []).find((x) => x.diagram_id === sp.diagram_id) || {};
                    const normalized = normalizeParca(sp);
                    return normalized ? { ...normalized, parca_id: h.parca_id } : { ...sp, parca_id: h.parca_id };
                }),
                rayic_deger: hesap.rayic_deger,
                hasar_faturasi: hasarFatura ? Number(hasarFatura) : undefined,
                hesap_yontemi: hesapYontemi,
                secili_yontem: hesap.secili_yontem || hesap.onerilen_yontem,
                rayic_hasarli: rayicHasarli ? Number(rayicHasarli) : hesap.rayic_hasarli,
                hesap_sonuc_json: hesap,
            });
            toast.success(`Kaydedildi — ${hesap.gercek_deger_kaybi_fmt}`);
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Kayıt başarısız");
        } finally {
            setLoading(false);
        }
    };

    const pdfIndir = async (opts = {}) => {
        const sablon = typeof opts === "string" ? opts : opts?.sablon || "hesap";
        const tahkimMode = typeof opts === "object" && opts?.tahkim_paket;
        const aktifHesap = hesap || ekspertizSonuc?.hesap;
        if (!aktifHesap) {
            toast.error("Önce hesap yapın veya ekspertiz raporu analiz edin");
            return;
        }
        setPdfLoading(true);
        try {
            const blob = await dkGelismis.pdf({
                hesap: aktifHesap,
                sablon,
                tahkim_paket: tahkimMode,
                meta: {
                    dosya_no: seciliDosya?.dosya_no,
                    plaka: seciliDosya?.plaka,
                    muvekkil: seciliDosya?.muvekkil_adi,
                    marka,
                    model,
                    dosya_id: seciliDosya?.id,
                    sigorta_sirketi: seciliDosya?.sigorta_sirketi,
                    karsilastirma: ekspertizSonuc?.karsilastirma,
                    aciklama: ekspertizSonuc?.aciklama,
                    rapor: ekspertizSonuc?.rapor,
                },
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const suffix = { hesap: "hesap", ucuncu_kisi: "ucuncu_kisi", bilirkisi_uzman: "bilirkisi" }[sablon] || sablon;
            a.download = `dk_${suffix}_${seciliDosya?.dosya_no || "rapor"}${tahkimMode ? "_tahkim" : ""}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            const etiket = {
                hesap: "Hesap raporu",
                ucuncu_kisi: "3. kişi bildirimi",
                bilirkisi_uzman: "Bilirkişi / uzman görüşü",
            }[sablon] || "PDF";
            toast.success(`${etiket} indirildi`);
        } catch (e) {
            toast.error(await parseBlobApiError(e, "PDF oluşturulamadı"));
        } finally {
            setPdfLoading(false);
        }
    };

    const whatsappGonder = async () => {
        if (!seciliDosya?.id || !hesap) {
            toast.error("Dosya seçili ve hesap yapılmış olmalı");
            return;
        }
        setLoading(true);
        try {
            const r = await dkGelismis.whatsapp({ dosya_id: seciliDosya.id, hesap });
            toast.success(r.detail || "WhatsApp kuyruğa eklendi");
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "WhatsApp gönderilemedi");
        } finally {
            setLoading(false);
        }
    };

    const tahkimPaketOlustur = async () => {
        if (!seciliDosya?.id) {
            toast.error("Önce dosya seçin");
            return;
        }
        setLoading(true);
        try {
            const r = await dkGelismis.tahkimPaket({
                sigorta_dosyasi_id: seciliDosya.id,
                dosya_no: seciliDosya.dosya_no,
                yeniden_hesapla: false,
            });
            if (!r.success) throw new Error(r.error || "Paket oluşturulamadı");
            setTahkimPaket(r);
            toast.success("Tahkim savunma paketi hazır");
            return r;
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Tahkim paketi oluşturulamadı");
        } finally {
            setLoading(false);
        }
    };

    const ocrYukle = async (file) => {
        setLoading(true);
        try {
            const r = await dkGelismis.ocr(file);
            if (!r.success) throw new Error(r.error || "OCR başarısız");
            applyGelismisForm(r.gelismis_form || r.parametreler || {}, { mesaj: "Evrak okundu" });
            if (r.hesap) {
                setHesap(r.hesap);
                toast.success(`Net DK: ${r.hesap.gercek_deger_kaybi_fmt || "hesaplandı"}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Evrak okunamadı");
        } finally {
            setLoading(false);
        }
    };

    const ekspertizRaporAnaliz = async (file, opts = {}) => {
        if (opts.formOnly?.gelismis_form) {
            applyGelismisForm(opts.formOnly.gelismis_form, { mesaj: "Kısmi form uygulandı" });
            setEkspertizSonuc(opts.formOnly);
            return;
        }
        if (!file) return;

        setLoading(true);
        setEkspertizSonuc(null);
        setHesap(null);
        setSenaryo(null);
        try {
            const r = await dkGelismis.ekspertizRapor(file, {
                dosya_id: seciliDosya?.id,
                piyasa_rayic_ara: true,
                kusur_orani: kusur !== "" ? Number(kusur) : undefined,
                hasar_durumu: hasarDurumu,
                vites_tip: vites,
                yakit_tip: yakit,
                tramer_tutar: tramer !== "" ? Number(tramer) : undefined,
                km_min: kmMin !== "" ? Number(kmMin) : undefined,
                km_max: kmMax !== "" ? Number(kmMax) : undefined,
            });
            setEkspertizSonuc(r);

            if (r.gelismis_form) {
                applyGelismisForm(r.gelismis_form, { mesaj: "Ekspertiz raporu okundu" });
            }
            if (r.hesap) {
                setHesap(r.hesap);
                if (r.hesap.rayic_arastirma) setRayicSonuc(r.hesap.rayic_arastirma);
                if (r.hesap.rayic_deger) setRayic(String(Math.round(Number(r.hesap.rayic_deger))));
                toast.success(`Net DK: ${r.hesap.gercek_deger_kaybi_fmt}`, { duration: 5000 });
            } else if (!r.success) {
                toast.warning(formatApiHata(r.error) || "Otomatik hesap tamamlanamadı — form kısmen dolduruldu");
            }
        } catch (e) {
            const err = e?.response?.data;
            const status = e?.response?.status;
            const msg =
                status === 502
                    ? "Sunucu geçici olarak yanıt veremedi (502). OCR işlemi uzun sürdü veya sunucu yeniden başladı — 1-2 dk bekleyip tekrar deneyin."
                    : formatApiHata(err?.error) || e.message || "Ekspertiz analizi başarısız";
            setEkspertizSonuc(err?.error ? err : { success: false, error: msg });
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const ekspertizTemizle = () => {
        setEkspertizSonuc(null);
    };

    const faturaOcrAc = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*,.pdf";
        input.onchange = (e) => {
            const f = e.target.files?.[0];
            if (f) ocrYukle(f);
        };
        input.click();
    };

    const temizleParcalar = () => {
        setSeciliParcalar([]);
        setHesap(null);
        setSenaryo(null);
    };

    return {
        loading,
        diagram,
        zones,
        sablonlar,
        gorunumler,
        diagramGorunum,
        setDiagramGorunum,
        seciliParcalar,
        hoverParca,
        setHoverParca,
        dosyalar,
        dosyaQ,
        setDosyaQ,
        seciliDosya,
        gecmisHesaplar,
        marka,
        setMarka,
        model,
        setModel,
        modelYili,
        setModelYili,
        km,
        setKm,
        kusur,
        setKusur,
        rayic,
        setRayic,
        hasarFatura,
        setHasarFatura,
        hasarDurumu,
        setHasarDurumu,
        vites,
        setVites,
        yakit,
        setYakit,
        kmMin,
        setKmMin,
        kmMax,
        setKmMax,
        tramer,
        setTramer,
        hesapYontemi,
        setHesapYontemi,
        rayicHasarli,
        setRayicHasarli,
        aracNiteligi,
        setAracNiteligi,
        rayicHasarliSonuc,
        rayicSonuc,
        hesap,
        senaryo,
        onizleme,
        seciliIlan,
        setSeciliIlan,
        adim,
        kmOner,
        parcaToggle,
        parcaIslemChange,
        dosyaSec,
        uygulaSablon,
        rayicAra,
        rayicHasarliAra,
        hesapla,
        senaryoHesapla,
        kaydet,
        tahkimPaket,
        setTahkimPaket,
        ekspertizdenDoldur,
        pdfIndir,
        pdfLoading,
        whatsappGonder,
        tahkimPaketOlustur,
        ocrYukle,
        ekspertizRaporAnaliz,
        ekspertizSonuc,
        ekspertizTemizle,
        faturaOcrAc,
        temizleParcalar,
        aracFiltreBody,
    };
}

export { normalizeParca };
